import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Chip, Paper, MenuItem, FormControl, InputLabel, Select, IconButton, Stack,
  InputAdornment,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { Add, Delete, Edit, Preview, ChevronRight, Search } from '@mui/icons-material';
import {
  getReportSchema, getReportDefinitions, createReportDefinition, updateReportDefinition,
  deleteReportDefinition, previewReport,
  ReportDefinition, ReportColumn, DataSourceSchema,
} from '../../services/reportService';

const MODULE_LABELS: Record<string, string> = {
  transportation: 'Transport',
  porter: 'Porter',
  on_demand: 'Services',
  wallet: 'Wallet & Finance',
  analytics: 'Users & CRM',
  subscriptions: 'Subscriptions',
  events: 'Events',
};

const MODULE_ORDER = ['transportation', 'porter', 'on_demand', 'wallet', 'analytics', 'subscriptions', 'events'];

function moduleLabel(module: string) {
  return MODULE_LABELS[module] || module.replace(/_/g, ' ');
}

function RunReportsTab() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const { data: definitions, isLoading } = useQuery({ queryKey: ['report-definitions'], queryFn: getReportDefinitions });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (definitions || []).filter((r) => {
      if (moduleFilter !== 'all' && r.module !== moduleFilter) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q)
        || r.slug.toLowerCase().includes(q)
        || String(r.module || '').toLowerCase().includes(q)
        || String(r.description || '').toLowerCase().includes(q)
      );
    });
  }, [definitions, search, moduleFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, ReportDefinition[]>();
    for (const r of filtered) {
      const key = r.module || 'other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return [...map.entries()].sort((a, b) => {
      const ai = MODULE_ORDER.indexOf(a[0]);
      const bi = MODULE_ORDER.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [filtered]);

  const moduleOptions = useMemo(() => {
    const set = new Set((definitions || []).map((r) => r.module).filter(Boolean));
    return ['all', ...MODULE_ORDER.filter((m) => set.has(m)), ...[...set].filter((m) => !MODULE_ORDER.includes(m))];
  }, [definitions]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search reports…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Module</InputLabel>
          <Select
            label="Module"
            value={moduleFilter}
            onChange={(e) => setModuleFilter(String(e.target.value))}
          >
            {moduleOptions.map((m) => (
              <MenuItem key={m} value={m}>
                {m === 'all' ? 'All modules' : moduleLabel(m)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {filtered.length} report{filtered.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {grouped.map(([module, items]) => (
        <Box key={module} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25, letterSpacing: '0.02em' }}>
            {moduleLabel(module)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {items.length}
            </Typography>
          </Typography>
          <Grid container spacing={2}>
            {items.map((r) => (
              <Grid item xs={12} sm={6} md={4} key={r._id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.25,
                    cursor: 'pointer',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2.5,
                    height: '100%',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
                  }}
                  onClick={() => navigate(`/reports/${r.slug}`)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="start" gap={1}>
                    <Typography fontWeight={700}>{r.name}</Typography>
                    {r.isSystem && <Chip label="System" size="small" />}
                  </Box>
                  {r.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.45 }}>
                      {r.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    {moduleLabel(r.module)} · {r.slug}
                  </Typography>
                  <Button size="small" endIcon={<ChevronRight />} sx={{ mt: 1.25, px: 0, textTransform: 'none', fontWeight: 700 }}>
                    Open report
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {!isLoading && !filtered.length && (
        <Typography color="text.secondary">
          {definitions?.length ? 'No reports match your search.' : 'No reports yet. Create one in Report Builder.'}
        </Typography>
      )}
    </Box>
  );
}

function BuilderTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    dataSource: 'rides',
    reportType: 'list' as 'list' | 'aggregate',
    groupBy: '',
    columns: [] as ReportColumn[],
  });

  const { data: schema } = useQuery({ queryKey: ['report-schema'], queryFn: getReportSchema });
  const { data: definitions, isLoading } = useQuery({ queryKey: ['report-definitions'], queryFn: getReportDefinitions });

  const selectedSource = schema?.find((s) => s.key === form.dataSource);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        slug: form.slug || undefined,
        module: selectedSource?.module,
        query: {
          dataSource: form.dataSource,
          type: form.reportType,
          groupBy: form.reportType === 'aggregate' ? form.groupBy : undefined,
          metrics: form.reportType === 'aggregate'
            ? [{ field: 'quantity', op: 'sum', alias: 'total' }]
            : undefined,
          limit: 500,
          sort: { field: 'createdAt', direction: 'desc' },
        },
        columns: form.columns,
        filters: [
          { key: 'dateFrom', label: 'From', field: 'createdAt', type: 'date', operator: 'gte' },
          { key: 'dateTo', label: 'To', field: 'createdAt', type: 'date', operator: 'lte' },
        ],
      };
      return editId ? updateReportDefinition(editId, payload) : createReportDefinition(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-definitions'] });
      setOpen(false);
      setEditId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteReportDefinition,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['report-definitions'] }),
  });

  const previewMutation = useMutation({
    mutationFn: () => previewReport({
      name: form.name,
      query: {
        dataSource: form.dataSource,
        type: form.reportType,
        groupBy: form.groupBy || undefined,
        limit: 25,
      },
      columns: form.columns,
    }),
  });

  const addColumn = (field: DataSourceSchema['fields'][0]) => {
    if (form.columns.some((c) => c.field === field.key)) return;
    setForm({
      ...form,
      columns: [...form.columns, {
        key: field.key.replace(/\./g, '_'),
        label: field.label,
        field: field.key,
        type: field.type,
      }],
    });
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', slug: '', dataSource: 'rides', reportType: 'list', groupBy: '', columns: [] });
    setOpen(true);
  };

  const openEdit = (r: ReportDefinition) => {
    if (r.isSystem) return;
    setEditId(r._id);
    const q = r.query as Record<string, unknown>;
    setForm({
      name: r.name,
      slug: r.slug,
      dataSource: (q.dataSource as string) || 'rides',
      reportType: (q.type as 'list' | 'aggregate') || 'list',
      groupBy: (q.groupBy as string) || '',
      columns: r.columns || [],
    });
    setOpen(true);
  };

  const defColumns: GridColDef[] = [
    { field: 'name', headerName: 'Report', flex: 1 },
    { field: 'slug', headerName: 'Slug', width: 160 },
    { field: 'module', headerName: 'Module', width: 120 },
    {
      field: 'isSystem', headerName: 'Type', width: 90,
      renderCell: (p) => <Chip label={p.value ? 'System' : 'Custom'} size="small" />,
    },
    {
      field: 'actions', headerName: '', width: 100,
      renderCell: (p) => !p.row.isSystem ? (
        <>
          <IconButton size="small" onClick={() => openEdit(p.row)}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => deleteMutation.mutate(p.row._id)}><Delete fontSize="small" /></IconButton>
        </>
      ) : null,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Report</Button>
      </Box>
      <SerialNoDataGrid rows={definitions || []} columns={defColumns} loading={isLoading} getRowId={(r) => r._id} autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 2, mb: 2 }} />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Report' : 'Create Report'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={6}><TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Slug (optional)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Data Source</InputLabel>
                <Select label="Data Source" value={form.dataSource} onChange={(e) => setForm({ ...form, dataSource: e.target.value, columns: [] })}>
                  {(schema || []).map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select label="Report Type" value={form.reportType} onChange={(e) => setForm({ ...form, reportType: e.target.value as 'list' | 'aggregate' })}>
                  <MenuItem value="list">List</MenuItem>
                  <MenuItem value="aggregate">Aggregate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.reportType === 'aggregate' && (
              <Grid item xs={12}>
                <TextField fullWidth label="Group By field" value={form.groupBy} onChange={(e) => setForm({ ...form, groupBy: e.target.value })} helperText="e.g. status, metricCode" />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Columns — click to add</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                {(selectedSource?.fields || []).map((f) => (
                  <Chip key={f.key} label={f.label} onClick={() => addColumn(f)} variant="outlined" size="small" />
                ))}
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {form.columns.map((c) => (
                  <Chip key={c.key} label={c.label} onDelete={() => setForm({ ...form, columns: form.columns.filter((x) => x.key !== c.key) })} />
                ))}
              </Box>
            </Grid>
            {previewMutation.data && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption">Preview: {previewMutation.data.meta.total} rows</Typography>
                  <pre style={{ fontSize: 11, maxHeight: 120, overflow: 'auto' }}>
                    {JSON.stringify(previewMutation.data.rows?.slice(0, 3), null, 2)}
                  </pre>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button startIcon={<Preview />} onClick={() => previewMutation.mutate()} disabled={!form.columns.length}>Preview</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={!form.name || !form.columns.length}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function ReportBuilderPage() {
  const [tab, setTab] = useState(0);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance & Reports"
        title="Reports"
        subtitle="Choose a report to open it on a dedicated page with filters, search, and export."
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Reports" />
        <Tab label="Report Builder" />
      </Tabs>
      {tab === 0 && <RunReportsTab />}
      {tab === 1 && <BuilderTab />}
    </PageShell>
  );
}
